<template>
  <button
    :type="type"
    :class="classList"
    :id="id"
    :disabled="disabled || isLoading"
    :tabindex="tabindex"
    :aria-label="ariaLabel"
    :aria-busy="isLoading || undefined"
    @mouseenter="isHoveredOn = true"
    @mouseleave="isHoveredOn = false"
  >
    <StSpinner
      v-if="isLoading"
      :loading-message="srLoadingMessage"
      small
      hide-loading-message
    />
    <template
      v-for="(child, index) in $slots.default ? $slots.default() : []"
      :key="index"
    >
      <template v-if="child?.type?.__name === 'Icon'">
        <component
          :is="child"
          :icon-props="{ color: iconColor, secondaryColor: iconColor }"
        />
      </template>
      <template v-else>
        <span>
          <component :is="child" />
        </span>
      </template>
    </template>
  </button>
</template>

<script setup>
import { ref, computed } from "vue";
import { StSpinner } from "@/index";
import useIconColor from "@/composables/useIconColor.js";

const props = defineProps({
  type: {
    type: String,
    default: "button",
    validator: function (type) {
      return ["submit", "button", "reset"].includes(type);
    },
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  tabindex: {
    type: String,
    default: "0",
  },
  secondary: {
    type: Boolean,
  },
  tertiary: {
    type: Boolean,
  },
  quaternary: {
    type: Boolean,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: "",
  },
  srLoadingMessage: {
    type: String,
    default: "",
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  small: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
  },
  rectangular: {
    type: Boolean,
  },
});

const buttonType = props.tertiary
  ? "tertiary"
  : props.secondary
  ? "secondary"
  : props.quaternary
  ? "quaternary"
  : "primary";

const classList = computed(() => {
  const button = "st-button";
  return [
    button,
    `${button}--${buttonType}`,
    props.small && `${button}--small`,
    props.rectangular && `${button}--rectangular`,
    props.fullWidth && `${button}--full-width`,
    props.isLoading && `${button}--loading`,
  ];
});

const isHoveredOn = ref(false);

const { iconColor } = useIconColor(
  buttonType,
  isHoveredOn,
  computed(() => props.disabled),
);
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";
@import "./button.scss";

.st-button {
  @include button-modifiers;
}
</style>
